/**
 * Geteilte Kleinteile der Sektion „Anfos & Umsatz" (P1–P3): Laden, Zahlformate, Kachel, Etikett, Bündel „Bisher
 * bezahlt", Quote, Veränderung, Vergleichsstil. Plan 2026-10-23-p1-p2-kpi-review-umbau, Stufe 0 (E7.2, P2-V3 1a).
 *
 * Herkunft: bis 23.10.2026 lagen diese Teile in `scenes/auswertungen/webinare-produkte/shared.tsx` (P1) und
 * `scenes/auswertungen/kanaele/shared.tsx` (P3). Beide Dateien re-exportieren sie weiter — die bisherigen Importe
 * bleiben gültig, das Verhalten ist unverändert (Code wortgleich übernommen). Neue Kapitel importieren von HIER.
 *
 * Neu und nur als Opt-in: `VergleichWert`/`VergleichEtikett` (Stil „Vergleich": helle Fläche, Etikett, Schrift
 * normal — Vergleichswerte sind nicht unsicher, also nicht grau; P1-V3 § 2.0 Regel 3b), `JungEtikett` (P2-V3 § 2.0/3),
 * `AbweichungMarke` mit eigenem `tip` (Zeitraum-Marke, `TERMS.abweichungZeitraumMarke`), `BezahltBuendel` generisch.
 */
import { FC, Fragment, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import InfoTip from '../InfoTip';
import {
  formatBerlinWallclock,
  Veraenderung,
  VergleichsFenster,
} from '../../helpers/auswertungenData';
import { formatPct, jungTip, TERMS } from '../../helpers/kpiDefinitions';
import { formatNumber } from '../../helpers/numberUtils';

// ─── Laden ──────────────────────────────────────────────────────────────────────────────────

/** Lädt einmal beim Mount; `fehler` statt Absturz. Abbruch-Flag gegen späte Antworten. */
export function useLaden<T>(fetcher: () => Promise<T>): { data: T | null; fehler: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetcher()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setFehler(e.message);
      });
    return () => {
      cancelled = true;
    };
    // fetcher ist eine Modulkonstante (fetchProdukte …), also stabil: lädt genau einmal.
  }, [fetcher]);
  return { data, fehler };
}

export const Laden: FC<{ fehler: string | null }> = ({ fehler }) =>
  fehler ? (
    <Alert severity="warning">Konnte nicht geladen werden: {fehler}</Alert>
  ) : (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress size={28} />
    </Box>
  );

/**
 * Breite eines Elements in px (ResizeObserver), als Callback-Ref; `weitere` bekommt dasselbe Element (z. B.
 * `useSpaltenwahl().messRef`, das die Breiten-STUFE misst). Die Kanal-/Blog-Tabelle wird damit unter
 * `ENG_UNTER` px enger gesetzt: bei 1.024 px Fenster mit offener Seitenleiste (Karte 654 px) lief sie sonst
 * 117 px über (gemessen 23.10.2026).
 */
export function useBreitePx(
  weitere?: (el: HTMLElement | null) => void,
): [(el: HTMLElement | null) => void, number | null] {
  const [px, setPx] = useState<number | null>(null);
  const beobachter = useRef<ResizeObserver | null>(null);
  const ref = useCallback(
    (el: HTMLElement | null) => {
      weitere?.(el);
      beobachter.current?.disconnect();
      beobachter.current = null;
      if (!el) return;
      setPx(el.getBoundingClientRect().width);
      if (typeof ResizeObserver === 'undefined') return;
      const ro = new ResizeObserver((e) => {
        const w = e[0]?.contentRect.width;
        if (w) setPx(w);
      });
      ro.observe(el);
      beobachter.current = ro;
    },
    [weitere],
  );
  return [ref, px];
}

/** Unter dieser Kartenbreite stehen Kanal- und Blog-Tabelle eng (kleinere Ränder, Sortierpfeil nur an der aktiven Spalte). */
export const ENG_UNTER = 860;

// ─── Zahlformate ────────────────────────────────────────────────────────────────────────────

export const int = (v: number | null | undefined): string =>
  v === null || v === undefined ? '–' : formatNumber(v, 0);

/** `2026-09-12 18:00:00` → `12.09.2026`. */
export const datum = (v: string | null | undefined): string =>
  v ? formatBerlinWallclock(v.slice(0, 10)) : '–';

/** „24.04.–22.10.2026" (ein Jahr) bzw. „23.10.2025–22.04.2026" (über den Jahreswechsel); Tage als YYYY-MM-DD. */
export function spanne(erster: string, letzter: string): string {
  const tm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
  return erster.slice(0, 4) === letzter.slice(0, 4)
    ? `${tm(erster)}–${tm(letzter)}${letzter.slice(0, 4)}`
    : `${tm(erster)}${erster.slice(0, 4)}–${tm(letzter)}${letzter.slice(0, 4)}`;
}

/** Satz zum Schnitt am Datenstand (für Tooltips der Veränderung). */
export const datenstandSatz = (v: VergleichsFenster): string =>
  v.datenstand
    ? `Beide Zeiträume enden am Datenstand von PL-X (${formatBerlinWallclock(v.datenstand)}): ${spanne(v.jetzt.erster_tag, v.jetzt.letzter_tag)} gegen die gewählte Basis.`
    : 'Beide Zeiträume enden am selben Tag.';

/** Euro-Summe in T€ mit einer Nachkommastelle („246,3 T€", vorschlag_v3 §2.0/9). */
export function tEuro(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${(v / 1000).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} T€`;
}

/** Veränderung in ganzen Prozent mit Vorzeichen („−18 %", „+39 %", „±0 %"); genauer Wert im Tooltip. */
export function formatDelta(v: number | null | undefined, stellen = 0): string {
  if (v === null || v === undefined) return '—';
  const r = Number(v.toFixed(stellen));
  const zahl = Math.abs(r).toLocaleString('de-DE', {
    minimumFractionDigits: stellen,
    maximumFractionDigits: stellen,
  });
  return `${r > 0 ? '+' : r < 0 ? '−' : '±'}${zahl} %`;
}

/** Quote je 1.000 mit zwei signifikanten Stellen (0,0090 · 0,13 · 4,2 · 11). */
export function jeTausend(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (v === 0) return '0';
  if (v >= 10) return formatNumber(v, 0);
  // Immer zwei geltende Stellen („3,0" neben „2,8", „0,060" neben „0,12") — mit nur maximumSignificantDigits stand
  // „3" neben „2,8" und „0,06" neben „0,035" (Persona-Runde 20.10.).
  return v.toLocaleString('de-DE', { minimumSignificantDigits: 2, maximumSignificantDigits: 2 });
}

/** Euro je 1.000: ab 10 € ganz, darunter zwei geltende Stellen (wie `jeTausend`), 0 ohne Cent. */
export function euroJeTausend(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (v === 0) return '0 €';
  if (v >= 10) return `${formatNumber(v, 0)} €`;
  return `${v.toLocaleString('de-DE', { minimumSignificantDigits: 2, maximumSignificantDigits: 2 })} €`;
}

/** Große Zahlen kompakt (mobil): 2.588.456 → „2,6 Mio.", 78.450 → „78 Tsd.". */
export function kompakt(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (v >= 1_000_000)
    return `${(v / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} Mio.`;
  if (v >= 10_000) return `${Math.round(v / 1000).toLocaleString('de-DE')} Tsd.`;
  return formatNumber(v, 0);
}

/** Geschützte Leerzeichen vor € und %, damit die Einheit in schmalen Zellen nicht allein umbricht. */
export const fest = (s: string): string => s.replace(/ (€|%)/g, ' $1');

// ─── Bausteine ──────────────────────────────────────────────────────────────────────────────

/** Kleiner Hinweistext unter einer Grafik oder Tabelle. */
export const Hinweis: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="body2"
    sx={{ color: 'text.secondary', mt: 1.5, maxWidth: '68ch', lineHeight: 1.6 }}
  >
    {children}
  </Typography>
);

/** Produkt als Kürzel + Name, kompakt für Tabellen. */
// Audit C3 (30.10.2026): Artikelnummern („2010005") stehen nicht vor dem Produktnamen — dann nur der Name, fett.
export const ProduktZelle: FC<{ code: string | null; name: string | null }> = ({ code, name }) =>
  code && /^\d{5,}$/.test(code) && name ? (
    <Box sx={{ lineHeight: 1.3, fontWeight: 600 }}>{name.replace(/^\d{5,}\s*[·:–-]?\s*/, '') || name}</Box>
  ) : (
    <Box sx={{ lineHeight: 1.3 }}>
      <Box component="span" sx={{ fontWeight: 600 }}>
        {code || '–'}
      </Box>
      {name && name !== code && (
        <Box component="span" sx={{ display: 'block', fontSize: 12, color: 'text.secondary' }}>
          {name}
        </Box>
      )}
    </Box>
  );

/**
 * Höhe eines liegenden Balkendiagramms: ~31 px je Zeile, bei `padding={0.3}` also höchstens 24 px Balken
 * (dataviz: Balken nie den Platz füllen lassen, der Rest ist Luft). Gemessen 07.10.: 34 px je Zeile gab 25 px.
 */
export const balkenHoehe = (n: number, rand = 70): number => Math.max(160, n * 31 + rand);

/** Höchstbreite eines Säulendiagramms: bei `padding={0.45}` bleibt jede Säule ≤ 24 px. */
export const saeulenBreite = (n: number, rand = 56): number => n * 43 + rand;

/**
 * Raster der Kacheln einer Karte (P5-Review #31): eine Regel für alle Karten — vorher `repeat(2, …)` in der
 * Cross-Sell-Karte und `auto-fill 200px` in „Verweigerte Bestellungen".
 */
export const KACHEL_RASTER = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))',
  gap: 2,
  mb: 2,
} as const;

/**
 * Befundzeile oben in einer Karte (P5-Review #17/#24): EIN Satz, beim Rendern aus den Daten gerechnet, beschreibend.
 * `fehlt`: warum es keinen Befund gibt (zu wenige Daten) — steht dann grau statt eines Satzes.
 */
/**
 * Spannen und Zeiträume („10–16 %", „01.01.–27.10.") nicht am Strich umbrechen: Wortverbinder (U+2060) beidseits des
 * Halbgeviertstrichs zwischen Ziffern bzw. Datumspunkt und Ziffer (Darstellungsprüfung Runde 2).
 */
export const spannenFest = (s: string): string => s.replace(/([\d.])–(\d)/g, '$1\u2060–\u2060$2');

export const BefundZeile: FC<{ text?: ReactNode; fehlt?: string | null; tip?: string }> = ({ text, fehlt, tip }) => (
  <Box
    sx={{
      borderLeft: 3,
      borderColor: fehlt ? 'divider' : 'info.main',
      pl: 1.5,
      py: 0.5,
      mb: 2,
      maxWidth: '75ch',
      fontSize: 13,
      lineHeight: 1.55,
      color: fehlt ? 'text.secondary' : 'text.primary',
    }}
  >
    <Box component="span" sx={{ fontWeight: 600 }}>
      {TERMS.befund.label}:
    </Box>{' '}
    {fehlt ?? (typeof text === 'string' ? spannenFest(text) : text)}
    <InfoTip text={tip ? `${TERMS.befund.tip} ${tip}` : TERMS.befund.tip} />
  </Box>
);

/** Eine Kennzahl im Kopf des Kapitels. */
export const Kachel: FC<{ wert: ReactNode; label: string; tip?: string; sub?: ReactNode }> = ({
  wert,
  label,
  tip,
  sub,
}) => (
  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: '8px', px: 2, py: 1.5, minWidth: 0 }}>
    <Typography
      sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}
    >
      {wert}
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: 'text.secondary', mt: 0.5, display: 'flex', alignItems: 'center' }}
    >
      {label}
      {tip && <InfoTip text={tip} />}
    </Typography>
    {sub && (
      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 12, mt: 0.25 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

/**
 * Teile einer Unterzeile, getrennt durch „ · ", umbrochen nur ZWISCHEN den Teilen — in schmalen Zellen (390 px)
 * brach sonst „nie / gez." oder „112 / Zahler" (gemessen 20.10.).
 */
export const Teile: FC<{ teile: ReactNode[]; sx?: object }> = ({ teile, sx }) => (
  <Box component="span" sx={{ display: 'block', ...sx }}>
    {/* Trenner AUSSERHALB der nicht trennbaren Teile — nur dort darf die Zeile umbrechen. */}
    {teile.map((t, i) => (
      <Fragment key={i}>
        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
          {t}
        </Box>
        {i < teile.length - 1 ? ' · ' : ''}
      </Fragment>
    ))}
  </Box>
);

/** Kleines Etikett am Zeilennamen („wenig Anfos") — nennt den Grund der Sortierspalte (vorschlag_v3 §2.0/3). */
export const Etikett: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{ ml: 0.75, fontSize: 11, fontWeight: 400, color: 'text.secondary', whiteSpace: 'nowrap' }}
  >
    {children}
  </Box>
);

/**
 * Etikett „jung" am Zeilennamen (P2-V3 § 2.0/3): mehr als `jungAnteil` der Anfos der Zeile sind jünger als `jungTage`.
 * Die Schwellen kommen aus der API (`jung_anteil`, `jung_tage`); ohne sie steht der allgemeine Satz aus TERMS.
 */
export const JungEtikett: FC<{ jungAnteil?: number | null; jungTage?: number | null }> = ({
  jungAnteil,
  jungTage,
}) => (
  <Tooltip title={jungTip(jungAnteil ?? null, jungTage ?? null)} arrow placement="top">
    <Box
      component="span"
      sx={{
        ml: 0.75,
        fontSize: 11,
        fontWeight: 400,
        color: 'text.secondary',
        whiteSpace: 'nowrap',
        cursor: 'help',
      }}
    >
      {TERMS.jung.short}
    </Box>
  </Tooltip>
);

/** „Anfos Ø 75 Tage alt" — das Alter der Anfos, keine Zahlungsdauer (Persona-Runde 20.10.: „Ø 75 T." las sich so). 26.10.: hinter „7 Zahler" las sich das Alter als Alter der Zahler. */
export const alterText = (alterTage: number | null | undefined): string =>
  alterTage === null || alterTage === undefined ? '–' : `Anfos Ø ${int(alterTage)} Tage alt`;

/** Tooltip-Zusatz der Bezahlt-Spalten: warum „bezahlte Anfos" mehr sind als das „x von y" der Bezahlquote. */
export const zahlerHinweis = (reifeTage: number): string =>
  `„Bezahlte Anfos“ zählt jede Anfo mit Zahlung über 1 €, auch jüngere als ${reifeTage} Tage — deshalb mehr als im „x von y“ der Bezahlquote, das nur Anfos ab ${reifeTage} Tagen zählt.`;

/**
 * „12 bezahlte Anfos" (P5-Review #11): bis 27.10. „12 Zahler (alle Alter)" — „Zahler" hieß im Hebel ein Kunde, hier
 * eine Anfo, und „(alle Alter)" war unerklärt. Die Erklärung (auch junge Anfos) steht im Tooltip (`zahlerHinweis`).
 */
export const bezahlteAnfosText = (n: number): string =>
  `${int(n)} ${n === 1 ? 'bezahlte Anfo' : TERMS.zahlerAlleAlter.short}`;

/**
 * „bezahlt" immer als Bündel mit bezahlten Anfos und Ø Alter (Till 14.10., vorschlag_v3 §5.4): bezahlt wächst mit dem
 * Alter. Eine Unterzeile, umbrochen nur zwischen den Teilen. Generisch (P2-V3 1a): Wert, bezahlte Anfos, Ø Alter.
 * `inline` (P5-Review #27): für Fließtext — alles in einer Zeile, Bündel in Klammern, kein Block im Satz.
 */
export const BezahltBuendel: FC<{ wert: string; zahler: number; alterTage: number | null; inline?: boolean }> = ({
  wert,
  zahler,
  alterTage,
  inline = false,
}) =>
  inline ? (
    <Box component="span">
      <b>{wert}</b>{' '}
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        ({bezahlteAnfosText(zahler)}
      </Box>
      {' · '}
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        {alterText(alterTage)})
      </Box>
    </Box>
  ) : (
    <Box sx={{ lineHeight: 1.25 }}>
      {wert}
      <Teile
        teile={[bezahlteAnfosText(zahler), alterText(alterTage)]}
        sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'normal' }}
      />
    </Box>
  );

/**
 * ▲/▼ an einer Bezahlquote: auffällig nach Korrektur für viele Vergleiche (Backend, P3-Review Nr. 11).
 * Ohne Marke ist der Unterschied nicht von Zufall zu trennen — deshalb steht sonst nichts.
 * `tip`: eigene Erklärung statt „gegen den Schnitt" (Zeitraum gegen Zeitraum: `TERMS.abweichungZeitraumMarke.tip`).
 */
export const AbweichungMarke: FC<{ marke?: 'up' | 'down' | null; tip?: string }> = ({
  marke,
  tip,
}) =>
  marke ? (
    <Box
      component="span"
      title={
        tip === undefined
          ? `${marke === 'up' ? 'Zahlt auffällig besser' : 'Zahlt auffällig schlechter'} als der Schnitt. ${TERMS.abweichungMarke.tip}`
          : `${marke === 'up' ? 'Auffällig höher' : 'Auffällig niedriger'}. ${tip}`
      }
      aria-label={
        tip === undefined
          ? marke === 'up'
            ? 'zahlt auffällig besser'
            : 'zahlt auffällig schlechter'
          : marke === 'up'
            ? 'auffällig höher'
            : 'auffällig niedriger'
      }
      sx={{ ml: 0.5, fontSize: 12, color: marke === 'up' ? '#2e7d32' : '#c62828', cursor: 'help' }}
    >
      {marke === 'up' ? '▲' : '▼'}
    </Box>
  ) : null;

/** Bezahlquote mit Nenner; unter 20 reifen Anfos grau (eine Handvoll Zahler entscheidet). `marke` nur in P3. */
export const Quote: FC<{
  rate: number | null;
  bezahlt: number;
  reif: number;
  marke?: 'up' | 'down' | null;
}> = ({ rate, bezahlt, reif, marke }) =>
  rate === null ? (
    <>–</>
  ) : (
    <Box sx={{ color: reif < 20 ? 'text.disabled' : 'inherit', lineHeight: 1.25 }}>
      {formatPct(rate)}
      <AbweichungMarke marke={marke} />
      {/* Grau gilt auch für „x von y" — sonst stand die Unterzeile dunkler als die ausgegraute Quote (Review 20.10.). */}
      <Box
        component="span"
        sx={{ display: 'block', fontSize: 11, color: reif < 20 ? 'inherit' : 'text.secondary' }}
      >
        {int(bezahlt)} von {int(reif)}
      </Box>
    </Box>
  );

/**
 * Anzahl Anfos, darunter klein „davon N vorläufig" — vorläufig aus PX-Data ergänzte Anfos zählen als Anfo,
 * aber in keiner Quote (`TERMS.vorlaeufig`, Till 14.10.2026). Ohne solche nur die Zahl.
 */
export const AnfosZelle: FC<{ n: number; vorlaeufig?: number }> = ({ n, vorlaeufig = 0 }) =>
  vorlaeufig > 0 ? (
    <Box sx={{ lineHeight: 1.25 }}>
      {int(n)}
      <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
        davon {int(vorlaeufig)} vorläufig
      </Box>
    </Box>
  ) : (
    <>{int(n)}</>
  );

/** Satz für den Hinweis unter einer Karte: „N vorläufig ergänzt — zählen als Anfo, nicht in Quoten." Leer ohne solche. */
export const vorlaeufigSatz = (n: number): string =>
  n > 0
    ? `${int(n)} ${TERMS.vorlaeufig.label.toLowerCase()} — ${n === 1 ? 'zählt' : 'zählen'} als Anfo, nicht in Quoten und nicht im Erwarteten Wert.`
    : '';

// ─── Veränderung ────────────────────────────────────────────────────────────────────────────

/** Zerlegung einer Veränderung für den Tooltip: „Anfos −21,3 %, je Anfo +4,2 % · Vergleich: 1.777 Anfos, 25 ohne Wert". */
export function veraenderungTip(v: Veraenderung, schwelle: number): string {
  const b = v.basis;
  const basisTeile = [`${int(b.anfos)} Anfos`];
  if (b.without_value > 0) basisTeile.push(`${int(b.without_value)} ohne Wert`);
  if (b.provisional > 0) basisTeile.push(`${int(b.provisional)} vorläufig (zählen nicht)`);
  const vergleich = `Vergleichszeitraum: ${basisTeile.join(', ')}.`;
  const grau = v.grau_grund
    ? ` Grau: unter ${int(schwelle)} Anfos in einem der beiden Zeiträume, der Wert kann am Zufall hängen.`
    : '';
  if (v.neu) return `Neu: im Vergleichszeitraum keine Anfo.${grau}`;
  if (v.delta === null)
    return `Nicht rechenbar: in einem der beiden Zeiträume keine Anfo mit erwartetem Wert. ${vergleich}${grau}`;
  const teile = [
    v.delta_anfos === null ? null : `Anfos ${formatDelta(v.delta_anfos, 1)}`,
    v.delta_je_anfo === null ? null : `je Anfo ${formatDelta(v.delta_je_anfo, 1)}`,
  ].filter(Boolean);
  return `${formatDelta(v.delta, 1)}${teile.length ? ` (${teile.join(', ')})` : ''}. ${vergleich}${grau}`;
}

/** Zellinhalt der Veränderung: Zahl, „neu" oder „—", jeweils mit Grund im Tooltip (Regel 2.0/8). */
export const VeraenderungWert: FC<{ v: Veraenderung; schwelle: number }> = ({ v, schwelle }) => (
  <Tooltip title={veraenderungTip(v, schwelle)} arrow placement="top">
    <Box component="span" sx={{ cursor: 'help' }}>
      {v.neu ? 'neu' : formatDelta(v.delta)}
    </Box>
  </Tooltip>
);

// ─── Vergleichsstil (P1-V3 § 2.0 Regel 3b) ──────────────────────────────────────────────────

/**
 * Helle Fläche für Vergleichswerte („Typischer Termin", „typisch 5,7 T€"). Literal statt Theme-Grau: die Theme-Greys
 * sind invertiert (`.claude/rules/react-frontend.md`); ein heller Blau-Schleier liest sich in beiden Modi als Fläche.
 */
export const VERGLEICH_FLAECHE = 'rgba(104, 112, 250, 0.08)';

/** Etikett „Vergleich" — klein, nicht grau gemeint: Vergleichswerte sind sicher, nur keine echte Zeile. */
export const VergleichEtikett: FC = () => (
  <Box
    component="span"
    sx={{
      mr: 0.75,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: 0.3,
      textTransform: 'uppercase',
      color: 'text.secondary',
      whiteSpace: 'nowrap',
    }}
  >
    Vergleich
  </Box>
);

/** Ein Vergleichswert im Fliesstext oder in einer Kachel: helle Fläche, Etikett „Vergleich", Schrift normal. */
export const VergleichWert: FC<{ children: ReactNode; tip?: string }> = ({ children, tip }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'baseline',
      px: 0.75,
      py: 0.125,
      borderRadius: '4px',
      backgroundColor: VERGLEICH_FLAECHE,
      color: 'text.primary',
    }}
  >
    <VergleichEtikett />
    {children}
    {tip && <InfoTip text={tip} />}
  </Box>
);

// ─── Spaltenwahl-Menü ───────────────────────────────────────────────────────────────────────

/** Spalten-Beschreibung fürs Menü in einer festen Gruppenreihenfolge (= Bedeutung); unbekannte Gruppen zuerst. */
export const nachGruppenReihenfolge = <M extends { gruppe: string }>(
  meta: M[],
  reihenfolge: readonly string[],
): M[] => {
  const rang = (g: string) => {
    const i = reihenfolge.indexOf(g);
    return i < 0 ? -1 : i;
  };
  return [...meta].sort((a, b) => rang(a.gruppe) - rang(b.gruppe));
};
